import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN, internalUrl } from './siteConfig';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';
import { computeScopeOutcome, PACKAGE_LABELS, servicesPackageHash } from './scopeEstimator';

const PATH = '/scope-estimator';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;

const SITE_TYPES = [
  { id: 'landing', label: 'Single landing or one-page marketing site' },
  { id: 'brochure', label: 'Multi-page brochure / services website (no shop checkout)' },
  { id: 'commerce', label: 'E‑commerce or online payments on my site' },
  { id: 'customapp', label: 'Custom web app, portal, or booking system (beyond a brochure site)' }
];

const PAGE_BANDS = [
  { id: '1', label: 'About one page' },
  { id: '2-5', label: '2–5 pages' },
  { id: '6-8', label: '6–8 pages' },
  { id: '9+', label: 'More than eight pages or a large programme' }
];

const INTEGRATION_OPTS = [
  { id: 'forms', label: 'Contact / lead forms' },
  { id: 'analytics', label: 'Analytics (e.g. traffic measurement)' },
  { id: 'payments', label: 'Taking card or wallet payments on-site' },
  { id: 'bookings', label: 'Bookings or scheduling' },
  { id: 'crm', label: 'CRM or membership login area' }
];

function ToggleChip({ checked, onChange, id, label }) {
  return (
    <label
      htmlFor={id}
      className={`flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 text-sm ${
        checked ? 'border-indigo-500 bg-indigo-50 text-slate-900' : 'border-slate-200 bg-white text-slate-800'
      }`}
    >
      <input id={id} type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

export default function ScopeEstimatorPage() {
  const sid = useId();
  const [step, setStep] = useState(0);
  const [siteType, setSiteType] = useState('');
  const [pageBand, setPageBand] = useState('');
  const [integrations, setIntegrations] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = 'Which website package fits? — scope wizard | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : '';
    if (desc) {
      desc.setAttribute(
        'content',
        'Indicative wizard: match your project to Starter, Growth, or Professional — or plan a discovery call for e‑commerce, apps, and larger builds. Not a quote. WebCodeCraft UK.'
      );
    }
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Website scope fit wizard',
      url: CANONICAL,
      isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN }
    });
    ld.setAttribute('data-page', 'scope-estimator');
    document.head.appendChild(ld);
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      if (desc) desc.setAttribute('content', prevDesc || '');
      ld.remove();
    };
  }, []);

  const toggleInt = (id) => {
    setIntegrations((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const canNext0 = siteType !== '';
  const canNext1 = pageBand !== '';
  const finish = () => {
    setResult(
      computeScopeOutcome({
        siteType,
        pageBand,
        integrations
      })
    );
    setStep(3);
  };

  const reset = () => {
    setStep(0);
    setSiteType('');
    setPageBand('');
    setIntegrations([]);
    setResult(null);
  };

  const servicesHref = (key) => `${SITE_ORIGIN}/services#${servicesPackageHash(key)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      <CookieBanner />
      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip">
        <section className="container mx-auto px-4 py-10 max-w-2xl" aria-labelledby="scope-h1">
          <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-medium text-indigo-600 mb-2">
              <Link to="/website-tax-calculator" className="underline underline-offset-2 hover:text-indigo-800">
                Tools
              </Link>
              <span className="mx-1 text-slate-400" aria-hidden="true">
                /
              </span>
              <span className="text-slate-700">Scope fit</span>
            </p>
            <h1 id="scope-h1" className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight mb-4">
              Which package might fit?
            </h1>
            <p className="text-slate-600 leading-relaxed mb-6 text-sm sm:text-base">
              Answer a few questions for an <strong>indicative</strong> pointer toward our published Starter, Growth, or
              Professional website packages — or toward a discovery call when the work sits outside those tiers. This is{' '}
              <strong>not a quote or contract</strong>; final scope is agreed after we talk.
            </p>
          </m.div>

          {step < 3 && (
            <div className="mb-4 text-sm text-slate-500" aria-live="polite">
              Step {step + 1} of 3
            </div>
          )}

          {step === 0 && (
            <fieldset className="space-y-4 border-0 p-0 m-0">
              <legend className="text-lg font-semibold text-slate-900 mb-3">What best describes the build?</legend>
              <div className="space-y-2">
                {SITE_TYPES.map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-start gap-3 cursor-pointer rounded-xl border p-4 ${
                      siteType === opt.id ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`${sid}-site`}
                      className="mt-1 text-indigo-600 focus:ring-indigo-500"
                      checked={siteType === opt.id}
                      onChange={() => setSiteType(opt.id)}
                    />
                    <span className="text-slate-800">{opt.label}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-primary mt-6"
                disabled={!canNext0}
                onClick={() => setStep(1)}
              >
                Next
              </button>
            </fieldset>
          )}

          {step === 1 && (
            <fieldset className="space-y-4 border-0 p-0 m-0">
              <legend className="text-lg font-semibold text-slate-900 mb-3">Rough page count?</legend>
              <div className="space-y-2">
                {PAGE_BANDS.map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-start gap-3 cursor-pointer rounded-xl border p-4 ${
                      pageBand === opt.id ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`${sid}-pages`}
                      className="mt-1 text-indigo-600 focus:ring-indigo-500"
                      checked={pageBand === opt.id}
                      onChange={() => setPageBand(opt.id)}
                    />
                    <span className="text-slate-800">{opt.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" className="btn btn-secondary" onClick={() => setStep(0)}>
                  Back
                </button>
                <button type="button" className="btn btn-primary" disabled={!canNext1} onClick={() => setStep(2)}>
                  Next
                </button>
              </div>
            </fieldset>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3">Integrations (select any)</h2>
              <p className="text-sm text-slate-600 mb-4">We use this to spot checkout-, app-, or CRM-heavy work.</p>
              <div className="flex flex-wrap gap-2">
                {INTEGRATION_OPTS.map((opt) => (
                  <ToggleChip
                    key={opt.id}
                    id={`${sid}-${opt.id}`}
                    label={opt.label}
                    checked={integrations.includes(opt.id)}
                    onChange={() => toggleInt(opt.id)}
                  />
                ))}
              </div>
              <div className="flex gap-3 mt-8">
                <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
                  Back
                </button>
                <button type="button" className="btn btn-primary" onClick={finish}>
                  See fit
                </button>
              </div>
            </div>
          )}

          {step === 3 && result && (
            <m.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-6"
              role="status"
            >
              <h2 className="text-xl font-bold text-slate-900 mb-3">Suggestion</h2>
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                Indicative only. Published monthly fees and inclusions on our Services page apply at signup; bespoke or
                out-of-tier work is quoted separately after discovery.
              </p>

              {result.outcome === 'contact' && (
                <div className="space-y-4">
                  <p className="text-slate-700">{result.reason}</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href={internalUrl('contact')} className="btn btn-primary text-center">
                      Plan a discovery call
                    </a>
                    {siteType === 'commerce' && (
                      <a href={internalUrl('ecommerce-web-apps')} className="btn btn-secondary text-center">
                        E‑commerce &amp; web apps
                      </a>
                    )}
                  </div>
                </div>
              )}

              {result.outcome === 'package' && (
                <div className="space-y-4">
                  <p className="text-slate-700">
                    Based on your answers, the closest <strong>published website package</strong> is{' '}
                    <strong>{PACKAGE_LABELS[result.packageKey]}</strong>. Compare inclusions and monthly fees on
                    Services, then get in touch if it looks like a match.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href={servicesHref(result.packageKey)} className="btn btn-primary text-center">
                      View {PACKAGE_LABELS[result.packageKey]} on Services
                    </a>
                    <a href={internalUrl('contact')} className="btn btn-secondary text-center">
                      Talk to us
                    </a>
                  </div>
                </div>
              )}

              <button type="button" className="mt-6 text-sm text-indigo-600 font-medium underline underline-offset-2" onClick={reset}>
                Start over
              </button>
            </m.div>
          )}
        </section>
        <Footer />
      </main>
    </div>
  );
}
